/**
 * @fileoverview Decides whether a failed Sales CRM command may already have taken effect.
 * @description A command that carries an `Idempotency-Key` must resend the SAME key while its
 * outcome is unknown (the server then recognises the replay instead of repeating side effects), and
 * may take a new key only after a definite refusal.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { ApiException } from '@/shared/api/api.exception'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'

/**
 * @description Whether a command failed without a definite answer from the server: a network
 * error, timeout or abort (no response), a `408`, any `5xx`, or `SERVICE_UNAVAILABLE`.
 * @param {unknown} error - The mutation's error.
 * @returns {boolean} True when the command may have been applied and its key must be kept.
 */
export function isUnknownOutcomeError(error: unknown): boolean {
  if (!(error instanceof ApiException)) return true
  if (error.code === ERROR_CODES.NETWORK_ERROR || error.code === ERROR_CODES.SERVICE_UNAVAILABLE) {
    return true
  }
  return error.status === undefined || error.status === 408 || error.status >= 500
}
